import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  Divider,
  InputAdornment,
  Chip,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Send as SendIcon,
  Save as SaveIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNotify, useGetList } from 'react-admin';
import { useBrand } from './ServerBrandProvider';

interface ServiceBlueprint {
  id: string;
  name: string;
  description?: string;
  basePrice: number;
  estimatedDuration?: number;
  active: boolean;
  taxable: boolean;
  qbAccountRef?: string;
}

interface LineItem {
  id?: string;
  serviceBlueprintId?: string;
  serviceBlueprint?: ServiceBlueprint;
  name: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  amount: number;
  estimatedDuration?: number;
  isCustom?: boolean;
}

interface Customer {
  id: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  email?: string;
}

interface EstimateData {
  id?: string;
  estimateNumber: string;
  customerId: string;
  customer?: Customer;
  title: string;
  issueDate: Date;
  validUntil: Date;
  lineItems: LineItem[];
  notes?: string;
  terms?: string;
  status: 'draft' | 'sent' | 'approved' | 'rejected' | 'expired';
}

interface EstimateBuilderProps {
  estimate?: EstimateData;
  onSave: (data: EstimateData, action: 'draft' | 'send') => Promise<void>;
  onDelete?: () => Promise<void>;
  isLoading?: boolean;
}

export const EstimateBuilder: React.FC<EstimateBuilderProps> = ({
  estimate,
  onSave,
  onDelete,
  isLoading = false,
}) => {
  const { brand } = useBrand();
  const notify = useNotify();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get customers for autocomplete - backend now sorts by displayName by default
  const { data: customers = [] } = useGetList<Customer>('customers', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'displayName', order: 'ASC' },
  });

  // Get service blueprints for line items
  const { data: serviceBlueprints = [], isLoading: isLoadingServices } =
    useGetList<ServiceBlueprint>('crm/service-blueprints', {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      filter: { active: true },
    });

  // Get tenant settings for tax rate and estimate numbering
  const {
    data: tenantSettings,
    isLoading: isLoadingTenantSettings,
    error: tenantSettingsError,
  } = useGetList('crm/tenant-settings', {
    pagination: { page: 1, perPage: 1 },
  });

  const currentTenantSettings = tenantSettings?.[0];

  // Form state
  const [formData, setFormData] = useState<EstimateData>({
    estimateNumber: estimate?.estimateNumber || `EST-${Date.now().toString(36).toUpperCase()}`,
    customerId: estimate?.customerId || '',
    customer: estimate?.customer,
    title: estimate?.title || '',
    issueDate: estimate?.issueDate || new Date(),
    validUntil: estimate?.validUntil || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    lineItems: estimate?.lineItems || [],
    notes: estimate?.notes || '',
    terms: estimate?.terms || 'Payment due within 30 days of estimate approval.',
    status: estimate?.status || 'draft',
  });

  // Update formData when estimate prop changes (for editing)
  useEffect(() => {
    if (estimate) {
      setFormData(prev => ({
        ...prev,
        estimateNumber: estimate.estimateNumber || prev.estimateNumber,
        customerId: estimate.customerId || prev.customerId,
        customer: estimate.customer || prev.customer,
        title: estimate.title || prev.title,
        issueDate: estimate.issueDate ? new Date(estimate.issueDate) : prev.issueDate,
        validUntil: estimate.validUntil ? new Date(estimate.validUntil) : prev.validUntil,
        lineItems: estimate.lineItems ? estimate.lineItems : prev.lineItems,
        notes: estimate.notes || prev.notes,
        terms: estimate.terms || prev.terms,
        status: estimate.status || prev.status,
      }));
    }
  }, [estimate]);

  // Find customer object from customerId when editing
  useEffect(() => {
    if (estimate?.customerId && customers.length > 0 && !formData.customer) {
      const customer = customers.find(c => c.id === estimate.customerId);
      if (customer) {
        setFormData(prev => ({
          ...prev,
          customer,
        }));
      }
    }
  }, [estimate?.customerId, customers, formData.customer]);

  // Tax enabled state - user can only enable/disable, not change rate
  const [taxEnabled, setTaxEnabled] = useState(true);

  // Update tax enabled when tenant settings load
  useEffect(() => {
    if (currentTenantSettings) {
      setTaxEnabled(currentTenantSettings.taxEnabled || true);
    }
  }, [currentTenantSettings]);

  // Show error if tenant settings are required but not available
  if (tenantSettingsError || (!isLoadingTenantSettings && !currentTenantSettings)) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Configuration Error
          </Typography>
          <Typography>
            Unable to load tenant settings. Please contact your administrator to configure tax rates
            and estimate numbering.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Early return if still loading critical data
  if (isLoadingServices || isLoadingTenantSettings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Calculate totals on demand without updating state
  const calculateTotals = () => {
    if (!formData.lineItems || formData.lineItems.length === 0) {
      return { subtotal: 0, taxAmount: 0, totalAmount: 0 };
    }

    const subtotal = formData.lineItems.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unitPrice) || 0;
      return sum + qty * price;
    }, 0);

    const taxRate = taxEnabled ? currentTenantSettings?.primaryTaxRate || 0 : 0;
    const taxAmount = subtotal * (taxRate / 100);
    const totalAmount = subtotal + taxAmount;

    return { subtotal, taxAmount, totalAmount, taxRate };
  };

  // Get calculated totals
  const { subtotal, taxAmount, totalAmount, taxRate } = calculateTotals();

  const handleCustomerChange = (customer: Customer | null) => {
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customerId: customer.id,
        customer,
        title: prev.title || `Estimate for ${customer.displayName}`,
      }));
    }
  };

  const handleLineItemChange = (index: number, field: keyof LineItem, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addServiceFromBlueprint = (serviceBlueprint: ServiceBlueprint) => {
    const newLineItem: LineItem = {
      serviceBlueprintId: serviceBlueprint.id,
      serviceBlueprint,
      name: serviceBlueprint.name,
      description: serviceBlueprint.description || '',
      quantity: 1,
      unitPrice: serviceBlueprint.basePrice,
      taxRate: 0, // Not used anymore, tax is global
      amount: serviceBlueprint.basePrice,
      estimatedDuration: serviceBlueprint.estimatedDuration,
      isCustom: false,
    };

    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, newLineItem],
    }));
  };

  const removeLineItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    try {
      setIsDeleting(true);
      await onDelete();
      setDeleteDialogOpen(false);
    } catch (error: any) {
      notify(`Error deleting estimate: ${error.message || 'Failed to delete estimate'}`, {
        type: 'error',
      });
      setIsDeleting(false);
    }
  };

  const handleSave = async (action: 'draft' | 'send') => {
    try {
      if (!formData.customerId) {
        notify('Please select a customer', { type: 'warning' });
        return;
      }

      if (formData.lineItems.length === 0) {
        notify('Please add at least one service to the estimate', { type: 'warning' });
        return;
      }

      if (formData.lineItems.some(item => !item.name.trim())) {
        notify('All line items must have a name', { type: 'warning' });
        return;
      }

      const {
        subtotal: calcSubtotal,
        taxAmount: calcTaxAmount,
        totalAmount: calcTotalAmount,
        taxRate: calcTaxRate,
      } = calculateTotals();

      const dataToSave = {
        ...formData,
        subtotal: calcSubtotal,
        taxAmount: calcTaxAmount,
        totalAmount: calcTotalAmount,
        taxRate: calcTaxRate,
        taxEnabled: taxEnabled,
        status: action === 'send' ? ('sent' as const) : ('draft' as const),
      };

      await onSave(dataToSave, action);

      notify(action === 'send' ? 'Estimate sent successfully!' : 'Estimate saved as draft', {
        type: 'success',
      });
    } catch (error) {
      notify('Error saving estimate', { type: 'error' });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background:
            brand?.theme?.headerGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={2} alignItems="center">
                <ReceiptIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {estimate ? 'Edit Estimate' : 'Create Estimate'}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={() => setPreviewOpen(true)}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  Preview
                </Button>
                {estimate && onDelete && (
                  <Button
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    Delete
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} lg={8}>
          {/* Basic Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <BusinessIcon color="primary" />
                Basic Information
              </Typography>
              
              {/* Show customer info in edit mode */}
              {estimate?.id && formData.customer && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Customer
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formData.customer.displayName}
                  </Typography>
                  {formData.customer.email && (
                    <Typography variant="body2" color="text.secondary">
                      {formData.customer.email}
                    </Typography>
                  )}
                </Box>
              )}

              <Grid container spacing={3}>
                {/* Only show customer field when creating new estimate */}
                {!estimate?.id && (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={customers}
                      getOptionLabel={option => option.displayName || ''}
                      value={formData.customer || null}
                      onChange={(_, value) => handleCustomerChange(value)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Customer"
                          required
                          placeholder="Search for a customer..."
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body1">{option.displayName}</Typography>
                            {option.email && (
                              <Typography variant="body2" color="text.secondary">
                                {option.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Estimate Number"
                    value={formData.estimateNumber}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, estimateNumber: e.target.value }))
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">#</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={formData.title}
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Website Development Project"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Issue Date"
                    value={formData.issueDate}
                    onChange={date => date && setFormData(prev => ({ ...prev, issueDate: date }))}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Valid Until"
                    value={formData.validUntil}
                    onChange={date => date && setFormData(prev => ({ ...prev, validUntil: date }))}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarIcon />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon color="primary" />
                  Services
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setServiceDialogOpen(true)}
                  size="small"
                >
                  Add Service
                </Button>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Qty</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Unit Price</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Amount</TableCell>
                      <TableCell sx={{ width: 50 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.lineItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No services added yet. Click &quot;Add Service&quot; to get started.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      formData.lineItems.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="standard"
                              value={item.name}
                              onChange={e => handleLineItemChange(index, 'name', e.target.value)}
                              placeholder="Service name"
                              sx={{ mb: 1 }}
                            />
                            <TextField
                              fullWidth
                              variant="standard"
                              value={item.description}
                              onChange={e =>
                                handleLineItemChange(index, 'description', e.target.value)
                              }
                              placeholder="Description (optional)"
                              multiline
                              maxRows={2}
                              InputProps={{
                                style: { fontSize: '0.875rem', color: 'text.secondary' },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              type="number"
                              value={item.quantity}
                              onChange={e =>
                                handleLineItemChange(index, 'quantity', Number(e.target.value))
                              }
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              type="number"
                              value={item.unitPrice}
                              onChange={e =>
                                handleLineItemChange(index, 'unitPrice', Number(e.target.value))
                              }
                              inputProps={{ min: 0, step: 0.01 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(item.quantity * item.unitPrice)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => removeLineItem(index)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Notes and Terms */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Internal notes (not visible to customer)..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Terms & Conditions"
                    multiline
                    rows={3}
                    value={formData.terms}
                    onChange={e => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                    placeholder="Payment terms, conditions..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar - Totals and Actions */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>

              {/* Status */}
              <Box mb={2}>
                <Chip
                  label={formData.status.toUpperCase()}
                  color={
                    formData.status === 'approved'
                      ? 'success'
                      : formData.status === 'sent'
                      ? 'info'
                      : formData.status === 'rejected'
                      ? 'error'
                      : formData.status === 'expired'
                      ? 'warning'
                      : 'default'
                  }
                  sx={{ mb: 2 }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Tax Setting */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={taxEnabled}
                      onChange={e => setTaxEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={`Apply Tax (${currentTenantSettings?.primaryTaxName || 'Tax'} ${
                    currentTenantSettings?.primaryTaxRate || 0
                  }%)`}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Tax rate is set by your organization
                </Typography>
              </Box>

              {/* Totals */}
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Subtotal:</Typography>
                  <Typography>{formatCurrency(subtotal)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>
                    Tax ({currentTenantSettings?.primaryTaxName || 'Tax'} {taxRate}%):
                  </Typography>
                  <Typography>{formatCurrency(taxAmount)}</Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Total:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatCurrency(totalAmount)}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 3 }} />

              {/* Actions */}
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<SaveIcon />}
                  onClick={() => handleSave('draft')}
                  disabled={isLoading}
                >
                  Save as Draft
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<SendIcon />}
                  onClick={() => handleSave('send')}
                  disabled={isLoading}
                  sx={{
                    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                    color: 'white',
                  }}
                >
                  Send Estimate
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Estimate Preview</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 3, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h5" gutterBottom>
              {formData.title}
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Estimate #{formData.estimateNumber}
            </Typography>

            <Grid container spacing={2} sx={{ my: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>To:</strong> {formData.customer?.displayName || 'Select Customer'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Valid Until:</strong> {formData.validUntil.toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>

            <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.lineItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {item.name}
                          </Typography>
                          {item.description && (
                            <Typography variant="caption" color="text.secondary">
                              {item.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell>{formatCurrency(item.quantity * item.unitPrice)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end">
              <Stack spacing={1} sx={{ minWidth: 200 }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Subtotal:</Typography>
                  <Typography>{formatCurrency(subtotal)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>
                    Tax ({currentTenantSettings?.primaryTaxName || 'Tax'} {taxRate}%):
                  </Typography>
                  <Typography>{formatCurrency(taxAmount)}</Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6">{formatCurrency(totalAmount)}</Typography>
                </Box>
              </Stack>
            </Box>

            {formData.terms && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Terms & Conditions:</strong>
                  <br />
                  {formData.terms}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Service Selection Dialog */}
      <Dialog
        open={serviceDialogOpen}
        onClose={() => setServiceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Service to Estimate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a service from your service blueprints to add to this estimate.
          </Typography>

          <Stack spacing={2}>
            {serviceBlueprints.map(service => (
              <Paper
                key={service.id}
                variant="outlined"
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                  border: '1px solid',
                  borderColor: 'divider',
                }}
                onClick={() => {
                  addServiceFromBlueprint(service);
                  setServiceDialogOpen(false);
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {service.name}
                    </Typography>
                    {service.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {service.description}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Chip
                        label={`${formatCurrency(service.basePrice)}`}
                        color="primary"
                        size="small"
                      />
                    </Stack>
                  </Box>
                  <Button variant="outlined" size="small" startIcon={<AddIcon />}>
                    Add
                  </Button>
                </Box>
              </Paper>
            ))}
          </Stack>

          {serviceBlueprints.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No active service blueprints found.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Create service blueprints in Configuration to get started.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <WarningIcon color="error" />
            <Typography variant="h6">Delete Estimate</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this estimate?
          </Typography>
          {estimate?.estimateNumber && (
            <Typography variant="body2" color="text.secondary">
              Estimate Number: <strong>{estimate.estimateNumber}</strong>
            </Typography>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. All data associated with this estimate will be permanently
            deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Estimate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
